/**
 * The user's choice of 3D-viewer backend (Preferences -> 3D viewer), remembered in this browser.
 *
 * Preferences are otherwise not persisted across page loads; this one is, because it decides
 * which backend the page constructs before anything else runs (the feature loader reads it).
 * Storage can be missing or throw (private windows, blocked site data, Node): every access is
 * guarded and a failure just means "no stored choice".
 */

export const VisualizerChoiceStorageKey = 'bmaps.visualizer';
/** Where the app goes when the chosen backend cannot run or fails to load. */
export const FallbackVisualizerBackend = '3dmol';

/** @returns {string|null} the stored backend name, or null */
export function readVisualizerChoice() {
    try {
        if (typeof localStorage === 'undefined') return null;
        return localStorage.getItem(VisualizerChoiceStorageKey) || null;
    } catch (ex) {
        return null;
    }
}

/** @param {string|null} name backend name to remember; null forgets the choice */
export function saveVisualizerChoice(name) {
    try {
        if (typeof localStorage === 'undefined') return;
        if (name) localStorage.setItem(VisualizerChoiceStorageKey, name);
        else localStorage.removeItem(VisualizerChoiceStorageKey);
    } catch (ex) {
        // storage unavailable: the choice lasts for this page only
    }
}

/**
 * Pick the backend for this page load: the `?visualizer=` URL parameter (testing), then the
 * stored choice, then the default. Unknown names, and backends that cannot run in this browser
 * (`isSupported()`), fall through with a console warning.
 * @param {Object<string, Function>} backends available backend classes by backendName
 * @param {Function} defaultBackend
 * @param {{search?: string}} [location]
 * @returns {Function} a backend class
 */
export function selectVisualizerBackend(backends, defaultBackend, location) {
    const loc = location || (typeof window !== 'undefined' ? window.location : null);
    const requested = loc ? new URLSearchParams(loc.search).get('visualizer') : null;
    const candidates = [
        ['URL parameter ?visualizer=', requested],
        ['stored preference', readVisualizerChoice()],
    ];
    for (const [source, name] of candidates) {
        if (!name) continue;
        const backend = backends[name.toLowerCase()];
        if (!backend) {
            console.warn(`Unknown visualizer "${name}" (${source}); available: ${Object.keys(backends).join(', ')}.`);
        } else if (typeof backend.isSupported === 'function' && !backend.isSupported()) {
            console.warn(`Visualizer "${name}" (${source}) cannot run in this browser (needs ${backend.requirement}).`);
        } else {
            if (backend !== defaultBackend) console.log(`Using 3D visualizer backend: ${backend.backendName} (${source})`);
            return backend;
        }
    }
    return defaultBackend;
}
